/**
 * Prospect-specific table cells, built on the shared kit. The generic helpers
 * (avatarColor/initials/money/moneyFull, LwPortal, useScrollLock) now
 * live in @/components/lw and are re-exported here so existing lo-prospects
 * imports keep working unchanged.
 */
import { LwStatusPill } from '@/components/lw';
import { LO_STAGE_META, type LoProspect, type LoProspectStage, type StageMeta } from '@/types/lo-prospect';

export { avatarColor, initials, money, moneyFull, LwPortal, useScrollLock } from '@/components/lw';

export function StageBadge({ stage }: { stage: LoProspectStage }) {
  // Runtime fallback: a stage value newer than this bundle (DB migrations land
  // before frontend deploys finish propagating) must degrade to a neutral
  // badge, not crash the pipeline.
  const meta = (LO_STAGE_META as Partial<Record<string, StageMeta>>)[stage]
    ?? { label: stage, badgeClass: 'badge--invited' };
  return <LwStatusPill label={meta.label} badgeClass={meta.badgeClass} />;
}

export function RunsDots({ used, stage }: { used: number; stage: LoProspectStage }) {
  const inactive = stage === 'registered' || stage === 'invited' || stage === 'passed';
  return (
    <div className="runs">
      <div className="runs-dots">
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} className={`rdot${i < used ? ' used' : ''}${inactive ? ' muted' : ''}`} />
        ))}
      </div>
      <div className="runs-label">
        <b>{used}</b> of 5 runs used
      </div>
    </div>
  );
}

export function BestQuote({ prospect }: { prospect: LoProspect }) {
  if (prospect.bestRate === null || prospect.runsUsed === 0) {
    return <span className="adv-empty">Not priced yet</span>;
  }
  return (
    <div className="adv">
      <div className="adv-main">{prospect.bestRate.toFixed(3)}%</div>
      <div className="adv-sub">
        @ {prospect.bestPrice?.toFixed(3)} · {prospect.displayBestInvestor ?? prospect.bestInvestor}
      </div>
    </div>
  );
}

export function RecruiterBadge({
  recruiterId,
  recruiterName
}: {
  recruiterId: string | null;
  recruiterName: string | null;
}) {
  if (!recruiterId || !recruiterName) {
    return <span className="rec-unassigned">Unassigned</span>;
  }
  const parts = recruiterName.trim().split(/\s+/);
  const init = `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
  return (
    <div className="rec">
      <span className="rec-av">{init}</span>
      <span className="rec-name">{recruiterName}</span>
    </div>
  );
}
